import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchTemplates, updateTemplate, resetTemplate } from '../api/client';
import { formatSequenceName } from '../utils/formatters';

const SAMPLE_DATA = {
  firstName: 'Alex',
  lastName: 'Johnson',
  companyName: 'Acme Corp',
  email: 'alex@acme.com',
  planType: 'pro',
  trialEndDate: '2026-04-01',
  unsubscribeUrl: 'https://app.vttd.tech/unsubscribe?preview=true',
  loginUrl: 'https://app.vttd.tech',
};

function interpolatePreview(text, data) {
  if (!text) return '';
  return text.replace(/\{\{(\w+)\}\}/g, (_m, key) => {
    const val = data[key];
    return val !== undefined && val !== null ? String(val) : '';
  });
}

// ---------------------------------------------------------------------------
// Body extraction / injection helpers
// ---------------------------------------------------------------------------

const BODY_MARKER = 'line-height:1.6;">';
const BODY_END = '<!-- Footer -->';

function extractBodyHtml(fullHtml) {
  const start = fullHtml.indexOf(BODY_MARKER);
  if (start === -1) return fullHtml;
  const contentStart = start + BODY_MARKER.length;
  const end = fullHtml.indexOf(BODY_END, contentStart);
  if (end === -1) return fullHtml;
  // Trim the </td></tr> right before the footer
  let body = fullHtml.slice(contentStart, end);
  body = body.replace(/\s*<\/td><\/tr>\s*$/, '');
  return body.trim();
}

function replaceBodyHtml(fullHtml, newBodyHtml) {
  const start = fullHtml.indexOf(BODY_MARKER);
  if (start === -1) return fullHtml;
  const contentStart = start + BODY_MARKER.length;
  const end = fullHtml.indexOf(BODY_END, contentStart);
  if (end === -1) return fullHtml;
  // Find the </td></tr> before footer
  const tdEnd = fullHtml.lastIndexOf('</td></tr>', end);
  const insertEnd = tdEnd !== -1 && tdEnd > contentStart ? tdEnd : end;
  return fullHtml.slice(0, contentStart) + '\n' + newBodyHtml + '\n' + fullHtml.slice(insertEnd);
}

function bodyHtmlToText(html) {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*/gi, '\n\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<\/?(table|tr|td|div|span|a|strong|b|em|i|h[1-6]|ul|ol|li|img|center)[^>]*>/gi, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&middot;/g, '\u00B7')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function textToBodyHtml(text) {
  return text
    .split('\n\n')
    .map((para) => {
      const inner = para.replace(/\n/g, '<br>');
      return `<p style="margin:0 0 16px 0;">${inner}</p>`;
    })
    .join('\n');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Templates() {
  const { getAccessTokenSilently } = useAuth0();
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Editor state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editorSubject, setEditorSubject] = useState('');
  const [editorFrom, setEditorFrom] = useState('');
  const [editorHtml, setEditorHtml] = useState('');
  const [editorText, setEditorText] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [editorMessage, setEditorMessage] = useState(null);

  // Debounced preview
  const [previewHtml, setPreviewHtml] = useState('');
  const debounceRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const result = await fetchTemplates(token);
      setTemplates(result.templates || result || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Debounced preview update
  useEffect(() => {
    if (!editingTemplate) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewHtml(interpolatePreview(editorHtml, SAMPLE_DATA));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [editorHtml, editingTemplate]);

  const openEditor = (tpl) => {
    const html = tpl.override?.html || tpl.rawHtml || tpl.preview?.html || '';
    const subject = tpl.override?.subject || tpl.subject || '';
    const from = tpl.override?.from || tpl.rawFrom || tpl.preview?.from || '';

    const bodyHtml = extractBodyHtml(html);
    const text = bodyHtmlToText(bodyHtml);

    setEditingTemplate(tpl);
    setEditorSubject(subject);
    setEditorFrom(from);
    setEditorHtml(html);
    setEditorText(text);
    setPreviewHtml(interpolatePreview(html, SAMPLE_DATA));
    setEditorMessage(null);
  };

  const closeEditor = () => {
    setEditingTemplate(null);
    setEditorMessage(null);
  };

  const handleTextChange = (text) => {
    setEditorText(text);
    const newBodyHtml = textToBodyHtml(text);
    setEditorHtml((prev) => replaceBodyHtml(prev, newBodyHtml));
  };

  const handleHtmlChange = (html) => {
    setEditorHtml(html);
  };

  // Re-sync text editor when user focuses it (picks up HTML editor changes)
  const handleTextFocus = () => {
    const bodyHtml = extractBodyHtml(editorHtml);
    setEditorText(bodyHtmlToText(bodyHtml));
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    setEditorMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await updateTemplate(token, editingTemplate.id, {
        subject: editorSubject,
        html: editorHtml,
        from: editorFrom,
      });
      setEditorMessage({ type: 'success', text: 'Template saved' });
      loadTemplates();
    } catch (err) {
      setEditorMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!editingTemplate) return;
    setResetting(true);
    setEditorMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await resetTemplate(token, editingTemplate.id);
      setEditorMessage({ type: 'success', text: 'Template reset to default' });
      const token2 = await getAccessTokenSilently();
      const result = await fetchTemplates(token2);
      const freshList = result.templates || result || [];
      setTemplates(freshList);
      const fresh = freshList.find((t) => t.id === editingTemplate.id);
      if (fresh) openEditor(fresh);
    } catch (err) {
      setEditorMessage({ type: 'error', text: err.message });
    } finally {
      setResetting(false);
    }
  };

  // ---------- Editor View ----------
  if (editingTemplate) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={closeEditor}
              className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary border-2 border-ink/30 rounded-xl hover:bg-surface transition-all duration-200 hover:-translate-y-0.5"
            >
              Back
            </button>
            <div>
              <h2 className="text-xl font-bold text-text-primary">{editingTemplate.id}</h2>
              <p className="text-sm text-text-secondary">
                {formatSequenceName(editingTemplate.sequence)} &middot; Step {editingTemplate.step}
                {editingTemplate.hasOverride && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-accent/20 text-accent">
                    Edited
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editingTemplate.hasOverride && (
              <button
                onClick={handleReset}
                disabled={resetting}
                className="px-4 py-1.5 text-sm font-semibold text-alert border-2 border-alert/30 rounded-xl hover:bg-alert/10 transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50"
              >
                {resetting ? 'Resetting...' : 'Reset to Default'}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-primary rounded-xl border-2 border-ink shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg active:translate-y-0.5 active:shadow-brutal-sm transition-all duration-200 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Message */}
        {editorMessage && (
          <div
            className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 ${
              editorMessage.type === 'success'
                ? 'bg-success/10 text-success border-success'
                : 'bg-alert/10 text-alert border-alert'
            }`}
          >
            {editorMessage.text}
          </div>
        )}

        {/* Subject + From */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-1">Subject</label>
            <input
              type="text"
              value={editorSubject}
              onChange={(e) => setEditorSubject(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-1">From</label>
            <input
              type="text"
              value={editorFrom}
              onChange={(e) => setEditorFrom(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            />
          </div>
        </div>

        {/* Top row: Plain Text Editor + Rendered Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: '400px' }}>
          {/* Left: Plain text editor */}
          <div className="flex flex-col">
            <label className="block text-sm font-semibold text-text-secondary mb-1">
              Plain Text Content
            </label>
            <p className="text-xs text-text-secondary mb-2">
              Edit the email copy here. {'{{variables}}'} are preserved. Changes update the HTML below.
            </p>
            <textarea
              value={editorText}
              onChange={(e) => handleTextChange(e.target.value)}
              onFocus={handleTextFocus}
              className="flex-1 w-full px-4 py-3 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary resize-none"
              spellCheck
              style={{ minHeight: '340px' }}
            />
          </div>

          {/* Right: Rendered preview */}
          <div className="flex flex-col">
            <label className="block text-sm font-semibold text-text-secondary mb-1">
              Delivery Preview
            </label>
            <p className="text-xs text-text-secondary mb-2">
              How the email will look in the inbox (with sample data).
            </p>
            <iframe
              srcDoc={previewHtml}
              title="Template Preview"
              className="flex-1 w-full rounded-xl border-2 border-ink/30 bg-white"
              sandbox="allow-same-origin"
              style={{ minHeight: '340px' }}
            />
          </div>
        </div>

        {/* Bottom: Full-width HTML editor */}
        <div className="flex flex-col">
          <label className="block text-sm font-semibold text-text-secondary mb-1">
            HTML Source
          </label>
          <p className="text-xs text-text-secondary mb-2">
            Full HTML with {'{{variable}}'} placeholders. Direct edits here override the text editor above.
          </p>
          <textarea
            value={editorHtml}
            onChange={(e) => handleHtmlChange(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary resize-y"
            spellCheck={false}
            style={{ minHeight: '400px', tabSize: 2 }}
          />
        </div>
      </div>
    );
  }

  // ---------- List View ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-text-secondary">Loading templates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-alert/10 border-2 border-alert rounded-xl p-6 shadow-brutal">
        <p className="text-alert font-semibold">Failed to load templates</p>
        <p className="text-text-secondary text-sm mt-1">{error}</p>
        <button onClick={loadTemplates} className="mt-3 text-sm text-primary font-medium hover:underline">
          Retry
        </button>
      </div>
    );
  }

  // Group templates by sequence
  const grouped = templates.reduce((acc, tpl) => {
    const key = tpl.sequence || 'unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(tpl);
    return acc;
  }, {});

  Object.values(grouped).forEach((group) => {
    group.sort((a, b) => (a.step || 0) - (b.step || 0));
  });

  const sequenceIds = Object.keys(grouped).sort();

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-extrabold text-text-primary">Templates</h2>

      {sequenceIds.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-text-secondary">No templates found.</p>
        </div>
      ) : (
        sequenceIds.map((seqId) => (
          <div key={seqId} className="space-y-3">
            <h3 className="text-lg font-bold text-text-primary">
              {formatSequenceName(seqId)}
            </h3>
            <div className="space-y-2">
              {grouped[seqId].map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={() => openEditor(tpl)}
                  className="w-full bg-surface rounded-xl border-2 border-ink shadow-brutal px-6 py-4 flex items-center justify-between text-left hover:-translate-y-0.5 hover:shadow-brutal-lg transition-all duration-200"
                >
                  <div className="flex items-center gap-4">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/20 text-primary text-xs font-bold shrink-0 border border-primary/30">
                      {tpl.step || '-'}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-text-primary">
                        {tpl.subject || tpl.id}
                        {tpl.hasOverride && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-accent/20 text-accent">
                            Edited
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-text-secondary mt-0.5">
                        {tpl.preview?.from && <span>From: {tpl.preview.from}</span>}
                        {tpl.preview?.from && tpl.id && <span> &middot; </span>}
                        <span className="font-mono">{tpl.id}</span>
                      </p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 text-text-secondary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
